import { Application, Container, Sprite, Text, BlurFilter, type TextStyleOptions } from "pixi.js";
import type { CosmosData } from "../parser/types.ts";
import type { NodeRT, SceneGraph, NodeDetail, NodeRef } from "./types.ts";
import { computeLayout } from "./layout.ts";
import { Camera } from "./Camera.ts";
import { getTextures } from "./textures.ts";
import { Starfield } from "./render/Starfield.ts";
import { Links, colorMap } from "./render/Links.ts";
import { MOTION } from "../config/cosmos.config.ts";

const GLOW_HALF = 128, SPHERE_HALF = 128, SPEC_HALF = 64, SUN_HALF = 256, CORONA_HALF = 512, HALO_HALF = 256;

export interface CosmosHooks {
  onFocus?: (gal: string | null) => void;
  onSelect?: (detail: NodeDetail | null) => void;
  onFrame?: (cam: Camera, scene: SceneGraph) => void;
}

/** Núcleo do Cosmos: monta a cena PixiJS e roda o loop cinematográfico. */
export class CosmosApp {
  app = new Application();
  cam!: Camera;
  scene!: SceneGraph;
  private tex = getTextures();
  private color: (g: string) => number;
  private world = new Container();
  private linksLayer: Links;
  private starfield = new Starfield();
  private dust = new Container();
  private nodes = new Container();
  private labels = new Container();
  private sunCorona!: Sprite;
  private sunHalo!: Sprite;
  private t0 = performance.now();

  // estado de interação
  hover: NodeRT | null = null;
  searchHit: NodeRT | null = null;
  focusNode: NodeRT | null = null;
  focusGal: string | null = null;
  private dimAmt = 0;
  private related: Set<string> | null = null;
  private relKey: string | null = null;
  active: Record<string, boolean> = {};
  private mirror: { stats?: Record<string, number> } | null = null;

  /** espelho fiel do Vault (scan completo) — usado p/ reconhecimento total no dashboard */
  setMirror(m: { stats?: Record<string, number> } | null) { this.mirror = m; }

  constructor(private data: CosmosData, private hooks: CosmosHooks = {}) {
    this.color = colorMap(data);
    this.linksLayer = new Links(data);
    for (const g of Object.keys(data.gal)) this.active[g] = true;
  }

  toggleGalaxy(g: string, on: boolean) { this.active[g] = on; }
  private isActive(n: NodeRT) { return n.kind === "council" || this.active[n.gal] !== false; }

  /** Sincronização ao vivo: recompõe a cena a partir de novos dados do Vault. */
  refresh(data: CosmosData) {
    this.data = data;
    this.color = colorMap(data);
    this.linksLayer.setData(data);
    for (const c of this.nodes.removeChildren()) c.destroy({ children: true });
    for (const c of this.dust.removeChildren()) c.destroy({ children: true });
    for (const c of this.labels.removeChildren()) c.destroy({ children: true });
    this.hover = null; this.searchHit = null; this.focusNode = null;
    this.focusGal = null; this.related = null; this.relKey = null;
    for (const g of Object.keys(data.gal)) this.active[g] = true;
    this.scene = computeLayout(data);
    this.buildNodes();
    this.hooks.onFocus?.(null);
  }

  /** pisca um aviso discreto de "sincronizado" */
  flashSync() {
    let el = document.getElementById("cosmos-sync");
    if (!el) {
      el = document.createElement("div");
      el.id = "cosmos-sync";
      document.body.appendChild(el);
    }
    el.textContent = "✦ sincronizado com o Vault";
    el.classList.remove("show"); void el.offsetWidth; el.classList.add("show");
  }

  async init(host: HTMLElement) {
    await this.app.init({
      background: "#04050b",
      resizeTo: window,
      antialias: true,
      resolution: Math.min(2, window.devicePixelRatio || 1),
      autoDensity: true,
      powerPreference: "high-performance",
    });
    host.appendChild(this.app.canvas);

    this.scene = computeLayout(this.data);
    this.cam = new Camera(this.app.renderer.width, this.app.renderer.height);

    // ordem de profundidade
    this.app.stage.addChild(this.starfield.gfx);
    this.world.addChild(this.linksLayer.gfx, this.dust, this.nodes);
    this.app.stage.addChild(this.world);
    this.app.stage.addChild(this.labels);

    this.dust.filters = [new BlurFilter({ strength: 4, quality: 3 })];

    this.buildNodes();
    let maxR = 0;
    for (const n of this.scene.rt) maxR = Math.max(maxR, Math.hypot(n.bx, n.by) + n.r);
    this.cam.fit(maxR);

    this.attachInput();
    this.app.ticker.add(() => this.frame());
  }

  private buildNodes() {
    for (const n of this.scene.rt) {
      const view = new Container();
      view.position.set(n.x, n.y);
      const col = this.color(n.gal);

      if (n.kind === "council") {
        const halo = new Sprite(this.tex.halo); halo.anchor.set(0.5);
        const corona = new Sprite(this.tex.corona); corona.anchor.set(0.5);
        const g1 = new Sprite(this.tex.glow); g1.anchor.set(0.5); g1.tint = 0xffd24a; g1.alpha = 0.5;
        const core = new Sprite(this.tex.sun); core.anchor.set(0.5);
        view.addChild(corona, g1, halo, core);
        this.sunCorona = corona; this.sunHalo = halo;
        n.glow = g1; n.core = core;
      } else {
        const glow = new Sprite(this.tex.glow); glow.anchor.set(0.5); glow.tint = col;
        const core = new Sprite(this.tex.sphere); core.anchor.set(0.5); core.tint = col;
        const spec = new Sprite(this.tex.spec); spec.anchor.set(0.5);
        view.addChild(glow, core, spec);
        n.glow = glow; n.core = core; n.spec = spec;
      }
      n.view = view;
      const sharp = n.kind !== "note" || n.soft > 0.62;
      (sharp ? this.nodes : this.dust).addChild(view);
    }
  }

  // ---------- loop ----------
  private frame() {
    const W = this.app.renderer.width / this.app.renderer.resolution;
    const H = this.app.renderer.height / this.app.renderer.resolution;
    const time = (performance.now() - this.t0) / 1000;
    this.cam.resize(W, H);
    this.cam.update();
    this.dimAmt += (((this.focusGal ? 1 : 0) - this.dimAmt) * MOTION.focusDimEase);

    // câmera -> transform do world
    this.world.scale.set(this.cam.z);
    this.world.position.set(W / 2 - this.cam.x * this.cam.z, H / 2 - this.cam.y * this.cam.z);

    // movimento orgânico
    const rot = time * MOTION.rotationSpeed, cr = Math.cos(rot), sr = Math.sin(rot);
    for (const n of this.scene.rt) {
      const rx = n.bx * cr - n.by * sr, ry = n.bx * sr + n.by * cr;
      const amp = n.kind === "council" ? 0 : Math.min(MOTION.driftAmp, 1.5 + n.depth * 1.3);
      n.x = rx + Math.cos(time * 0.22 + n.seed) * amp;
      n.y = ry + Math.sin(time * 0.19 + n.seed * 1.3) * amp;
      const want = this.hover === n || this.searchHit === n ? 1 : 0;
      n.hl += (want - n.hl) * MOTION.hoverEase;
    }

    const dimmed = this.hover || this.searchHit;
    const galDim = (n: NodeRT) =>
      this.focusGal && n.gal !== this.focusGal && n.kind !== "council" ? 1 - 0.9 * this.dimAmt : 1;

    // nós
    for (const n of this.scene.rt) {
      const v = n.view!;
      const vis = this.isActive(n);
      v.visible = vis;
      if (!vis) continue;
      v.position.set(n.x, n.y);
      const R = n.r * (1 + 0.05 * Math.sin(time * n.bspeed + n.seed)) * (1 + 0.14 * n.hl);
      let a = (n.kind === "council" ? 1 : n.soft) * galDim(n);
      if (dimmed && !this.isRel(n)) a *= 0.18;

      if (n.kind === "council") {
        const pulse = 1 + 0.045 * Math.sin(time * MOTION.sunPulseSpeed);
        n.core!.scale.set((R * pulse) / SUN_HALF);
        n.glow!.scale.set((R * 3.0 * pulse) / GLOW_HALF); n.glow!.alpha = 0.5;
        this.sunHalo.scale.set((R * 1.4 * pulse) / HALO_HALF);
        this.sunCorona.scale.set((R * 3.2 * pulse) / CORONA_HALF);
        this.sunCorona.rotation = time * 0.03;
        continue;
      }
      n.core!.tint = this.color(n.gal); n.core!.scale.set(R / SPHERE_HALF); n.core!.alpha = a;
      n.glow!.tint = this.color(n.gal);
      n.glow!.scale.set((R * (2.3 + n.hl)) / GLOW_HALF);
      n.glow!.alpha = (0.45 + 0.35 * n.hl) * a;
      const sharp = n.soft > 0.62;
      n.spec!.visible = sharp;
      if (sharp) {
        n.spec!.scale.set((R * 0.5) / SPEC_HALF);
        n.spec!.position.set(-R * 0.32, -R * 0.36);
        n.spec!.alpha = 0.5 * a;
      }
    }

    this.linksLayer.update(this.scene, this.cam, time, this.related, this.color, this.focusGal, this.dimAmt, (n) => this.isActive(n));
    this.starfield.update(this.cam, time, W, H);
    this.updateLabels(W, H, !!dimmed, galDim);
    this.hooks.onFrame?.(this.cam, this.scene);
  }

  // ---------- labels ----------
  private labelStyle(n: NodeRT): TextStyleOptions {
    const council = n.kind === "council";
    return {
      fontFamily: "Inter, sans-serif",
      fontSize: council ? 21 : n.kind === "galaxy" ? 15 : 13,
      fontWeight: council ? "800" : n.kind === "galaxy" ? "600" : "500",
      letterSpacing: council ? 3.5 : 0.3,
      fill: council ? 0xffe9a6 : 0xeef2fc,
    };
  }
  private updateLabels(_W: number, _H: number, dimmed: boolean, galDim: (n: NodeRT) => number) {
    const z = this.cam.z;
    for (const n of this.scene.rt) {
      if (!this.isActive(n)) { if (n.labelText) n.labelText.visible = false; continue; }
      const isH = this.hover === n || this.searchHit === n;
      const tier = n.kind === "council" ? 3 : n.kind === "galaxy" ? 2 : n.depth === 2 ? 1 : 0;
      const zoomShow = (tier === 1 && z > 0.95) || (tier === 0 && z > 1.55);
      const focShow = this.focusGal != null && n.gal === this.focusGal && n.depth <= 3 && this.dimAmt > 0.5;
      const hiddenByFocus = !!this.focusGal && this.dimAmt > 0.4 && n.gal !== this.focusGal && n.kind !== "council" && !isH;
      const show = !hiddenByFocus && (!n.tpl || isH) && (tier >= 2 || zoomShow || focShow || isH);
      if (!show) { if (n.labelText) n.labelText.visible = false; continue; }

      let a = (n.kind === "council" ? 1 : n.soft) * galDim(n);
      if (dimmed && !this.isRel(n)) a = 0.12;
      if (a < 0.07) { if (n.labelText) n.labelText.visible = false; continue; }

      if (!n.labelText) {
        const txt = n.kind === "council" ? n.label.toUpperCase() : n.label;
        n.labelText = new Text({ text: txt, style: this.labelStyle(n) });
        n.labelText.anchor.set(0.5);
        this.labels.addChild(n.labelText);
      }
      const lbl = n.labelText;
      lbl.visible = true;
      lbl.alpha = Math.min(1, 0.62 + 0.38 * n.hl) * a;
      const [px, py] = this.cam.worldToScreen(n.x, n.y);
      const R = n.r * this.cam.z;
      if (n.kind === "galaxy") {
        const oa = Math.atan2(n.y, n.x);
        lbl.position.set(px + Math.cos(oa) * (R + 14), py + Math.sin(oa) * (R + 14));
      } else {
        lbl.position.set(px, py + R + (n.kind === "council" ? 22 : 12));
      }
    }
  }

  // ---------- relacionamento (highlight) ----------
  private isRel(n: NodeRT): boolean {
    const h = this.hover || this.searchHit;
    if (!h) return true;
    if (this.relKey !== h.id) {
      this.related = new Set([h.id]);
      if (h.parent) this.related.add(h.parent);
      for (const c of this.scene.kids.get(h.id) ?? []) this.related.add(c.id);
      this.relKey = h.id;
    }
    return this.related!.has(n.id);
  }

  // ---------- interação ----------
  pick(sx: number, sy: number): NodeRT | null {
    let best: NodeRT | null = null, bd = 1e9;
    for (const n of this.scene.rt) {
      if (!this.isActive(n)) continue;
      const [px, py] = this.cam.worldToScreen(n.x, n.y);
      const d = Math.hypot(px - sx, py - sy), rr = Math.max(n.r * this.cam.z, 7);
      if (d < rr && d < bd) { bd = d; best = n; }
    }
    return best;
  }

  setFocus(n: NodeRT | null) {
    this.focusNode = n;
    this.focusGal = n && n.kind !== "council" ? n.gal : null;
    if (n) this.cam.focusOn(n.x, n.y, Math.min(2.8, Math.max(this.cam.z, (Math.min(this.cam.viewW, this.cam.viewH) * 0.42) / (n.sr + 30))));
    this.hooks.onFocus?.(this.focusGal);
    this.hooks.onSelect?.(n ? this.buildDetail(n) : null);
  }

  selectById(id: string) { const n = this.scene.byId.get(id); if (n) this.setFocus(n); }
  detailOf(id: string): NodeDetail | null { const n = this.scene.byId.get(id); return n ? this.buildDetail(n) : null; }

  private ref(n: NodeRT): NodeRef { return { id: n.id, label: n.label, gal: n.gal, kind: n.kind }; }
  private buildDetail(n: NodeRT): NodeDetail {
    const gm = this.data.gal[n.gal];
    const parent = n.parent ? this.scene.byId.get(n.parent) ?? null : null;
    const children = (this.scene.kids.get(n.id) ?? [])
      .filter((c) => !c.tpl).sort((a, b) => b.r - a.r).slice(0, 80).map((c) => this.ref(c));
    const connections: NodeRef[] = [];
    for (const e of this.data.links) {
      const other = e.s === n.id ? e.t : e.t === n.id ? e.s : null;
      if (other) { const o = this.scene.byId.get(other); if (o) connections.push(this.ref(o)); }
    }
    return {
      id: n.id, label: n.label, kind: n.kind, gal: n.gal,
      galLabel: gm?.label ?? n.gal, galColor: gm?.color ?? "#ffffff",
      peso: n.peso, path: n.path, meta: n.meta ?? {},
      childCount: n.childCount ?? 0, linkCount: n.linkCount ?? 0,
      parent: parent ? this.ref(parent) : null,
      children, connections: connections.slice(0, 80),
      trail: this.ancestors(n),
      metrics: this.metricsFor(n),
    };
  }

  /** KPIs vivos calculados do próprio Vault (dashboards). */
  private metricsFor(n: NodeRT): { label: string; value: string }[] {
    if (n.kind === "note") return [];
    const notes = this.scene.rt.filter((x) => x.kind === "note" && !x.tpl);
    if (n.kind === "council") {
      const galaxies = this.scene.rt.filter((x) => x.kind === "galaxy").length;
      const companies = notes.filter((x) => x.peso === 45).length;
      const orphans = notes.filter((x) => (x.linkCount ?? 0) === 0 && (x.childCount ?? 0) === 0).length;
      const out = [
        { label: "Galáxias", value: String(galaxies) },
        { label: "Empresas", value: String(companies) },
        { label: "Nós", value: String(notes.length) },
        { label: "Conexões", value: String(this.data.links.length) },
        { label: "Órfãos", value: String(orphans) },
      ];
      const s = this.mirror?.stats; // espelho fiel do Vault
      if (s) {
        out.push({ label: "Arquivos", value: String(s.arquivos_totais ?? 0) });
        out.push({ label: "Canvases", value: String(s.canvases ?? 0) });
        out.push({ label: "Pastas", value: String(s.folders ?? 0) });
        out.push({ label: "Tags", value: String(s.tags_unicas ?? 0) });
      }
      return out;
    }
    const inGal = notes.filter((x) => x.gal === n.gal);
    const idset = new Set(inGal.map((x) => x.id));
    const internal = this.data.links.filter((e) => idset.has(e.s) && idset.has(e.t)).length;
    const subareas = (this.scene.kids.get(n.id) ?? []).filter((c) => !c.tpl).length;
    const orphans = inGal.filter((x) => (x.linkCount ?? 0) === 0).length;
    const mats = inGal.map((x) => parseInt(x.meta?.maturidade ?? "")).filter((v) => !Number.isNaN(v));
    const out = [
      { label: "Nós", value: String(inGal.length) },
      { label: "Subáreas", value: String(subareas) },
      { label: "Conexões", value: String(internal) },
      { label: "Órfãos", value: String(orphans) },
    ];
    if (mats.length) out.push({ label: "Maturidade", value: `${Math.round(mats.reduce((a, b) => a + b, 0) / mats.length)}%` });
    return out;
  }

  /** caminho Sol→…→nó (para breadcrumbs) */
  ancestors(n: NodeRT): NodeRef[] {
    const out: NodeRef[] = [];
    let cur: NodeRT | undefined = n;
    const guard = new Set<string>();
    while (cur && !guard.has(cur.id)) {
      guard.add(cur.id);
      out.push(this.ref(cur));
      cur = cur.parent ? this.scene.byId.get(cur.parent) : undefined;
    }
    return out.reverse();
  }
  reset() {
    let mr = 0; for (const n of this.scene.rt) mr = Math.max(mr, Math.hypot(n.bx, n.by) + n.r);
    this.cam.fit(mr);
    this.focusNode = null; this.focusGal = null;
    this.hooks.onFocus?.(null); this.hooks.onSelect?.(null);
  }
  zoomBy(factor: number) { this.cam.nudgeZoom(factor); }
  get selectionId(): string | null { return this.focusNode?.id ?? null; }
  hasNode(id: string): boolean { return this.scene.byId.has(id); }
  nodeRef(id: string): NodeRef | null {
    const n = this.scene.byId.get(id);
    return n ? this.ref(n) : null;
  }
  queryNodes(q: string): NodeRef[] {
    const s = q.toLowerCase().trim();
    if (!s) return [];
    const rank: Record<string, number> = { council: 0, galaxy: 1, note: 2 };
    return this.scene.rt
      .filter((n) => !n.tpl && n.label.toLowerCase().includes(s))
      .sort((a, b) => {
        const sa = a.label.toLowerCase().startsWith(s) ? 0 : 1;
        const sb = b.label.toLowerCase().startsWith(s) ? 0 : 1;
        return sa - sb || rank[a.kind] - rank[b.kind] || b.r - a.r;
      })
      .map((n) => this.ref(n));
  }

  private attachInput() {
    const c = this.app.canvas;
    let drag: { x: number; y: number; px: number; py: number; moved: boolean } | null = null;
    c.addEventListener("mousedown", (e) => { drag = { x: e.clientX, y: e.clientY, px: e.clientX, py: e.clientY, moved: false }; this.cam.dragging = true; });
    window.addEventListener("mouseup", () => {
      if (drag && !drag.moved) { const h = this.pick(drag.x, drag.y); this.setFocus(h); }
      drag = null; this.cam.dragging = false;
    });
    window.addEventListener("mousemove", (e) => {
      if (drag) {
        if (Math.hypot(e.clientX - drag.x, e.clientY - drag.y) > 3) drag.moved = true;
        this.cam.panBy(e.clientX - drag.px, e.clientY - drag.py);
        drag.px = e.clientX; drag.py = e.clientY;
      }
      const h = this.pick(e.clientX, e.clientY);
      if (h !== this.hover) { this.hover = h; this.relKey = null; }
      c.style.cursor = h ? "pointer" : drag ? "grabbing" : "grab";
    });
    c.addEventListener("wheel", (e) => { e.preventDefault(); this.cam.zoomAt(e.clientX, e.clientY, Math.exp(-e.deltaY * 0.0012)); this.focusNode = null; this.focusGal = null; this.hooks.onFocus?.(null); }, { passive: false });
    c.addEventListener("dblclick", () => this.reset());
  }
}
